import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GlobalContants } from 'src/app/common/constants/global.contants';
import { SharedService } from 'src/app/common/services/shared.service';
import { ReportsChartEnum } from 'src/app/reports/models/enums/reports.enum';
import { UnitTypeEnum } from '../../../reports-lists/models/unit-type-enum';
import moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-land-price-analysis',
  templateUrl: './land-price-analysis.component.html',
  styleUrls: ['./land-price-analysis.component.scss'],
})
export class LandPriceAnalysisComponent {
  @Input() dropdownLabel: string = '';
  @Input() graphData: any;
  @Input() chartTitle: any = '';
  @Input() SectionId: string = '';
  @Input() sqmLabel = false;

  @Input() reportType: 'price' | 'rental' = 'price';
  @Input() showDropdown: boolean = true;
  @Input() chartLoader = false;

  @Output() filterChartdata = new EventEmitter();

  trendsDropdown: any = [];
  chartView = ReportsChartEnum;
  priceChartData: any = [];
  selectedChartId: any = '';
  chartId = '';
  chartOverviewData: any[] = [];
  trendsData: any;
  trendsText: string = '';
  minDateOnwards: any;

  selected_item=[];

  languageSub$: Subscription = new Subscription();

  constructor(
    private datePipe: DatePipe,
    private sharedService: SharedService,
    private translate: TranslateService
  ) { 
    this.languageSub$ = this.sharedService.language$.subscribe(lang => {
      this.transformData();
    })

  }

  ngOnChanges(changes: SimpleChanges) {    
    if(this.graphData){
      this.trendsDropdown = this.graphData?.dropdown_data || [];
    }
    this.createChartData();
    this.transformData();
  
  }

  ngOnInit() {
      this.chartId = this.SectionId;
  }

  createChartData() {
    if(this.graphData){
    const obj = this.graphData;
    let rawData = obj.quarterly_rates || [];

    const parseQuarter = (q: string) => {
      const match = q?.match(/^Q(\d)-(\d{4})$/);
      return match ? { q: +match[1], y: +match[2] } : { q: 0, y: 0 };
    };

    rawData = rawData
      .filter((e: any) => !!e.quarter)
      .sort((a: any, b: any) => {
        const A = parseQuarter(a.quarter);
        const B = parseQuarter(b.quarter);
        return A.y === B.y ? A.q - B.q : A.y - B.y;
      });

    // 🔹 Keep a Set of existing quarters to avoid duplicates
    const existingQuarters = new Set(rawData.map((d: any) => d.quarter));

    // Step 2: Fill gaps only **between existing quarters**
    const filled: any[] = [];
    for (let i = 0; i < rawData.length; i++) {
      const current = rawData[i];
      filled.push({ category: current.quarter, value: current.avg_rate });

      if (i < rawData.length - 1) {
        const next = rawData[i + 1];
        const currQ = parseQuarter(current.quarter);
        const nextQ = parseQuarter(next.quarter);

        let y = currQ.y;
        let q = currQ.q + 1;

        while (y < nextQ.y || (y === nextQ.y && q < nextQ.q)) {
          if (q > 4) {
            q = 1;
            y++;
          }

          const missingQuarter = `Q${q}-${y}`;

          // ✅ Only push if NOT already in rawData
          if (!existingQuarters.has(missingQuarter)) {
            filled.push({ category: missingQuarter, value: current.avg_rate });
          }

          q++;
        }
      }
    }

    this.priceChartData = filled;
  }else{
    this.priceChartData = [];
  }
  }



  getSymbolByType(type: UnitTypeEnum): string {
    let symbol = '';

    if (type === UnitTypeEnum.currencywithsqm) {
      symbol = ` ${this.translate.instant(
        this.sharedService.getStoreValue(GlobalContants.StoreKey.CurrencyCode)
      )}/${this.translate.instant(
        GlobalContants.keySymbolContants.squareMeterUnit
      )}`;
    }

    if (type === UnitTypeEnum.currency) {
      symbol = ` ${this.translate.instant(
        this.sharedService.getStoreValue(GlobalContants.StoreKey.CurrencyCode)
      )}`;
    }

    if (type === UnitTypeEnum.percentage) {
      symbol = GlobalContants.keySymbolContants.percentage;
    }
    return symbol;
  }

  transformData() {
    if (this.graphData) {
      if(!this.selected_item.length){
        this.selected_item = this.graphData?.dropdown_data.map((opt: any) => opt.name);
        this.selectedChartId = this.graphData?.dropdown_data.map((opt: any) => opt.label[this.sharedService.getCurrentLangID()]);
      }else{
        this.selectedChartId = this.selected_item.map((e: string) =>
          this.trendsDropdown.find((f: any) => f.name == e)?.label[this.sharedService.getCurrentLangID()]
        );
      }
      const summary = this.graphData.summary;
      const minDate = summary?.min_date ? moment.utc(summary.min_date).local().format('DD MMM YYYY') : null;
      const maxDate = summary?.max_date ? moment.utc(summary.max_date).local().format('DD MMM YYYY') : null;
      this.minDateOnwards = summary?.min_date ? `${moment.utc(summary?.min_date).local().format('MMM YYYY')} ${this.translate.instant('onwards')}` : '';

      const startingLabel = this.reportType === 'rental' ? 'reports.StartingRental' : 'reports.StartingPrice';
      const currentLabel = this.reportType === 'rental' ? 'reports.CurrentRental' : 'reports.CurrentPrice';

      this.chartOverviewData = [
        {
          label: 'reports.DateRange',
          value: ( minDate && maxDate) ?  `${minDate} to ${maxDate}` : null,
        },
        {
          label: startingLabel,
          value: {
            text: summary.start_price || null,
            symbol: this.getSymbolByType(UnitTypeEnum.currencywithsqm),
          },
        },
        {
          label: currentLabel,
          value: {
            text: summary.current_price || null,
            symbol: this.getSymbolByType(UnitTypeEnum.currencywithsqm),
          },
        },
        {
          label: 'reports.CumulativeGrowth',
          value: {
            text: summary.cumulative_growth || null,
            symbol: this.getSymbolByType(UnitTypeEnum.percentage),
          },
        },
        {
          label: 'reports.AnnualGrowth',
          value: {
            text: summary.annual_growth || null,
            symbol: this.getSymbolByType(UnitTypeEnum.percentage),
          },
        },
      ];
      // this.chartOverviewData.splice(0, 1);
    } else {
      this.chartOverviewData = [];
      this.minDateOnwards = '';
    }
  }

  trendsdropDownChange(event: any) {
    if(event){
      this.selected_item = event?.split(',');
      this.selectedChartId = this.selected_item.map((e: string) =>
        this.trendsDropdown.find((f: any) => f.name == e)?.label[this.sharedService.getCurrentLangID()]
      );
      this.chartId = `${this.SectionId}_${event}`;
      this.filterChartdata.emit(event);
    }

  }

    ngOnDestroy(): void{
    this.languageSub$.unsubscribe()
    }
}
